import { api } from '../../../lib/api';
import type { PagedResult } from '../../administration/users/userTypes';
import type {
  MyTrainingRecommendation,
  MyTrainingRecommendationAccount,
  MyTrainingRecommendationDetail,
} from './myTrainingRecommendationTypes';

/** What the register may be narrowed by. */
export interface MyTrainingRecommendationListQuery {
  search?: string;
  page?: number;
  pageSize?: number;
  sort?: string;
  /** Whether the survey it came from is live — the shell's key. */
  status?: string;
  /** `SurveyTrainingDecision` names. Its own axis because `status` is the shell's. */
  decision?: string;
}

function toQueryString(query: MyTrainingRecommendationListQuery): string {
  const params = new URLSearchParams();

  if (query.search?.trim()) params.set('search', query.search.trim());
  if (query.page && query.page > 1) params.set('page', String(query.page));
  if (query.pageSize) params.set('pageSize', String(query.pageSize));
  if (query.sort) params.set('sort', query.sort);

  // Sent whenever PRESENT, empty string included — "none of these" matches no row.
  if (query.status !== undefined) params.set('status', query.status);
  if (query.decision !== undefined) params.set('decision', query.decision);

  const serialized = params.toString();
  return serialized ? `?${serialized}` : '';
}

const BASE = '/api/self-service/training-recommendations';

/**
 * The training recommendations sent to the signed-in employee — read only, on
 * `my-training-recommendations.read`.
 *
 * **THERE IS NO SCOPE, SURVEY OR RESPONDENT PARAMETER, and that is not an omission.** Whose
 * recommendations come back is fixed by the ROUTE server-side; one not sent to the caller is a 404.
 * Answering one, recording its training and reporting on it are `mySurveysApi`'s, on Survey's keys —
 * and a report's files are served only there.
 */
export const myTrainingRecommendationsApi = {
  list: (query: MyTrainingRecommendationListQuery, signal?: AbortSignal) =>
    api<PagedResult<MyTrainingRecommendation>>(`${BASE}${toQueryString(query)}`, { signal }),

  get: (id: string, signal?: AbortSignal) => api<MyTrainingRecommendationDetail>(`${BASE}/${id}`, { signal }),

  /** 200 either way; `error` says why this account is sent none. */
  me: (signal?: AbortSignal) => api<MyTrainingRecommendationAccount>(`${BASE}/me`, { signal }),
};
